import { readonly, shallowRef } from "vue";

import { projects as projectsApi } from "~/api";

/**
 * Composable for fetching the paginated projects list as JSON.
 *
 * @returns {{
 *   projects: import('vue').DeepReadonly<import('vue').ShallowRef<Array>>,
 *   pagination: import('vue').DeepReadonly<import('vue').ShallowRef<object|null>>,
 *   loading: import('vue').DeepReadonly<import('vue').ShallowRef<boolean>>,
 *   error: import('vue').DeepReadonly<import('vue').ShallowRef<string|null>>,
 *   fetchProjects: Function,
 *   fetchCount: Function,
 *   clearError: Function
 * }}
 */
export function useProjects() {
  const _projects = shallowRef([]);
  const _pagination = shallowRef(null);
  const _loading = shallowRef(false);
  const _error = shallowRef(null);

  /**
   * Fetch projects list from the server.
   *
   * @param {{ active?: boolean, project_name?: string, page?: number, sort_by?: string, sort_direction?: string }} params
   */
  async function fetchProjects(params = {}) {
    _loading.value = true;
    _error.value = null;

    try {
      const data = await projectsApi.all({ query: params });
      _projects.value = data.projects;
      _pagination.value = data.pagination;
    } catch (err) {
      _error.value = err.message || "Oops, something went wrong!";
    } finally {
      _loading.value = false;
    }
  }

  function clearError() {
    _error.value = null;
  }

  /**
   * Fetch only the total count for a given filter without updating the
   * main projects list.  Uses per_page=1 so the payload is minimal.
   *
   * @param {{ active?: boolean }} params
   * @returns {Promise<number|null>}
   */
  async function fetchCount(params = {}) {
    try {
      const data = await projectsApi.all({ query: { ...params, per_page: 1 } }); // eslint-disable-line camelcase
      return data.pagination?.count ?? null;
    } catch {
      return null;
    }
  }

  return {
    clearError,
    error: readonly(_error),
    fetchCount,
    fetchProjects,
    loading: readonly(_loading),
    pagination: readonly(_pagination),
    projects: readonly(_projects)
  };
}
